import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IGenerateReportReq, IReport, ReportingFilterData, FilterList } from '../IReportingFilterData';
import { SsrsReportsService } from '@shared/services/SSRSreports/ssrsReports.service';
import { LoadingService } from '@shared/utils/loading.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ExporterService } from '@shared/services/exporter/exporter.service';

export interface FieldFilterReporting {
  label: string;
  type: string;
  value: string;
  options?: { key: string; value: string }[];
}

@Component({
  selector: 'app-reporting-filter',
  templateUrl: './reporting-filter.component.html',
  styleUrl: './reporting-filter.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReportingFilterComponent {
  //#region VARIABLES
  reportingDataFilter: ReportingFilterData;
  reportOrigin: 'Operaciones' | 'Solicitudes' | '' = '';
  numeroOperacion = '';
  numeroSolicitud = '';

  fields: FieldFilterReporting[];
  values: string[] = [];
  reportType: number = 0; // 1 = Escenario 1, 2 = Escenario 2, etc.
  csv: string = '';

  tableData: any[] = [];
  tableColumns: string[] = [];

  file: any;
  noReport = false;
  errorReport = false;

  // Guardar referencia a la ventana original para reportes que tardan
  private originalWindow: Window = window;
  private pendingReports: { url: string; timestamp: number }[] = [];

  //#endregion
  //#region ON INIT
  constructor(
    public dialogRef: MatDialogRef<ReportingFilterComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private ssrsReports: SsrsReportsService,
    private cdr: ChangeDetectorRef,
    public loading: LoadingService,
    private _snackBar: MatSnackBar,
    private _exportService: ExporterService
  ) {
    this.reportingDataFilter = data;
    if (this.reportingDataFilter.direct) {
      this.generateReport([]);
      return;
    }

    if (this.data.csv) {
        this.csv = this.data.csv;
        this.handleBase64Csv(this.csv);
    }

    if (this.data.numOpe) {
      this.numeroOperacion = this.data.numOpe;
      this.reportOrigin = 'Operaciones';
    } else if (this.data.numSol) {
      this.numeroSolicitud = this.data.numSol;
      this.reportOrigin = 'Solicitudes';
    }

    console.warn('reportingDataFilter', this.reportingDataFilter);
    this.loadConfigReport(this.reportingDataFilter.report);
  }

  loadConfigReport(reportData: IReport) {
    console.log('reportData', reportData);
    this.fields = this.parseProcedureMetadata(reportData);
    console.log('fields', this.fields);
    this.fillOptions(this.fields);
    
    // Cargar valores iniciales si se pasaron en data.initialParameters
    if (this.data.initialParameters && Array.isArray(this.data.initialParameters)) {
      this.loadInitialParameters(this.data.initialParameters);
    }
    
    // Auto-generar reporte si se especificó autoGenerate
    if (this.data.autoGenerate === true) {
      setTimeout(() => {
        this.generateReport(this.fields);
      }, 100);
    }
  }
  
  /**
   * Carga valores iniciales en los campos del formulario
   * @param initialParameters Array de {name, value}
   */
  private loadInitialParameters(initialParameters: { name: string; value: any }[]): void {
    initialParameters.forEach(param => {
      const field = this.fields.find(f => f.label.toLowerCase() === param.name.toLowerCase());
      if (field) {
        field.value = param.value !== null && param.value !== undefined ? String(param.value) : '';
      }
    });
    
    // Actualizar el array de valores
    this.values = this.fields.map((field) => field.value);
    
    // Ejecutar detección de cambios después del ciclo actual
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 0);
  }

  /**
   * Extrae los campos de un reporte (procedimiento y tipos de inputs)
   * y devuelve un array con la siguiente estructura:
   * {
   *     label: string,
   *     type: string,
   *     value: string
   * }
   * @param data Informaci n del reporte
   * @returns Array con los campos extraidos
   *
   * Existen 2 Escenarios:
   * 1. Reporte con filterList: Trae las variables entre corchetes []
   * 2. Reporte sin filterList: Trae las variables sin corchetes, separadas por comas
   */
  parseProcedureMetadata(data: any): { label: string; type: string; value: string; options: any[]; isNull: boolean }[] {
    const labels: string[] = [];
    const isNullFlags: boolean[] = [];
    let types: string[] = [];

    // Extraer tipos usando RegEx
    const inputsMatch = data.inputsType.match(/\[(.*?)\]/g);
    if (inputsMatch) {
      types.push(...inputsMatch.map((t) => t.replace(/[\[\]]/g, '')));
    }

    // Extraer labels (parámetros) manteniendo posiciones de null
    const matches = [...data.procedureMetadata.matchAll(/\[([^\]]+)\]/g)].map((m) => m[1]);
    matches.forEach(match => {
      if (match && match !== 'null' && match !== 'undefined' && match.trim() !== '') {
        labels.push(match);
        isNullFlags.push(false);
      } else {
        labels.push(match || 'null');
        isNullFlags.push(true);
      }
    });

    // Detectar si hay un valor sin corchetes al final
    const afterLastBracket = data.procedureMetadata.split(']').pop();
    if (afterLastBracket) {
      const tailParams = afterLastBracket
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      tailParams.forEach((param) => {
        if (!labels.includes(param)) {
          labels.push(param); // Lo añadimos como label si no estaba
        }
      });
    }

    // Ajustar tipos para que coincidan con la cantidad de labels
    if (labels.length !== types.length) {
      console.warn('Desajuste entre labels y types:', labels, types);
      
      if (labels.length > types.length) {
        // Si hay más labels que tipos, repetir el último tipo
        const lastType = types[types.length - 1] || 'varchar';
        while (types.length < labels.length) {
          types.push(lastType);
        }
        console.log('Extendiendo tipos:', types);
      } else if (labels.length < types.length) {
        // Si hay más tipos que labels, tomar solo los necesarios
        types = types.slice(0, labels.length);
        console.log('Recortando tipos:', types);
      }
    }
    
    // Aplicar detección de fechas a todos los tipos
    types = types.map((type, index) => {
      if (type === 'varchar' && this.isDateField(labels[index])) {
        return 'date';
      }
      return type;
    });
    console.log('Tipos finales con detección de fechas:', types);

    return labels.map((label, index) => ({
      label,
      type: types[index] || 'varchar', // Default a varchar si no hay tipo
      value: isNullFlags[index] ? 'null' : '', // Si es null, valor por defecto
      options: [],
      isNull: isNullFlags[index]
    }));
  }

  fillOptions(fields) {
    this.values = fields.map((field) => field.value);

    fields.forEach((field) => {
      if (!this.reportingDataFilter.report.filterList) {
        return;
      }
      const matchingFilter = this.reportingDataFilter.report.filterList.find((fl) => fl.parameter.toLowerCase() === field.label.toLowerCase());
      field.options = matchingFilter ? matchingFilter.list : [];
    });
  }

  //#endregion

  //#region ACCIONES REPORTES

  /**
   * Limpia todos los datos del reporte anterior
   */
  private clearPreviousReportData(): void {
    // Limpiar datos de la tabla
    this.tableData = [];
    this.tableColumns = [];
    
    // Limpiar archivo anterior
    this.file = null;
    
    // Resetear estados de error y reporte
    this.noReport = false;
    this.errorReport = false;
    
    // Forzar detección de cambios
    this.cdr.detectChanges();
  }

  generateReport(fields: any[]) {
    // Limpiar datos anteriores antes de generar nuevo reporte
    this.clearPreviousReportData();
    
    this.loading.show();
    this.errorReport = false;
    fields === undefined ? (fields = []) : fields;
    
    // Obtener todos los campos (incluyendo nulls) desde el parseo original
    const allFields = this.parseProcedureMetadata(this.reportingDataFilter.report);
    
    const payload: IGenerateReportReq = {
      report: this.reportingDataFilter.reportId,
      format: this.reportingDataFilter.typeFile,
      download: this.reportingDataFilter.typeFile === 'CSV' ? false : true,
      parameters: []
    };

    // Enviar todos los parámetros en el orden correcto
    allFields.forEach((field) => {
      let value = '';
      
      if (field.isNull) {
        value = 'null';
      } else {
        // Buscar el valor en los campos del formulario
        const formField = fields.find(f => f.label === field.label);
        if (formField) {
          value = this.convertDateFormat(formField.value, field.type);
        }
      }
      
      payload.parameters.push({
        name: field.label,
        value: value
      });
    });

    // Mostrar mensaje de espera para reportes que pueden tardar
    this._snackBar.open('Generando reporte... Esto puede tomar varios minutos.', 'Entendido', {
      duration: 5000
    });

    // Timeout para reportes que tardan mucho
    const timeoutId = setTimeout(() => {
      this._snackBar.open('El reporte está tardando más de lo esperado. Se abrirá automáticamente cuando esté listo.', 'Entendido', {
        duration: 8000
      });
    }, 30000); // 30 segundos

    this.ssrsReports.GenerateReport(payload).subscribe({
      next: (res) => {
        clearTimeout(timeoutId);
        this.loading.hide();
        console.log('Respuesta del reporte:', res);
        if (res.success) {
          if (res.file) {
            if (this.reportingDataFilter.typeFile === 'PDF') {
              this.toPdf(res.file);
              return
            }
            this.file = res.file;
            this.handleBase64Csv(res.file);
          } else if (res.url) {
            this.openReportInNewTab(res.url);
          } else {
            // Caso especial: success true pero sin archivo ni URL indica que no hay datos
            this._snackBar.open('No hay datos disponibles para los criterios de búsqueda seleccionados', 'cerrar', {
              duration: 5000
            });
            console.log('No hay datos disponibles para el reporte:', res);
            this.noReport = true;
          }
        } else {
          console.error('Error al generar el reporte:', res);
          this._snackBar.open('Se ha generado un error al procesar el reporte', 'cerrar', {
            duration: 5000
          });
          this.noReport = true;
          this.errorReport = true;
          this.cdr.detectChanges();
        }
      },
      error: (err) => {
        clearTimeout(timeoutId);
        this.loading.hide();
        console.log(err);
        this._snackBar.open('Error al generar el reporte. Inténtelo nuevamente.', 'cerrar', {
          duration: 5000
        });
      }
    });
  }

  handleBase64Csv(base64String: string) {
    if (!base64String) {
      console.error('El archivo Base64 está vacío');
      return;
    }

    const csvText = atob(base64String);

    const rows = csvText
      .split('\n')
      .map((row) => row.trim())
      .filter((row) => row);

    if (rows.length === 0) {
      console.error('El CSV está vacío');
      return;
    }

    this.tableColumns = rows[0].split(',');
    this.tableData = rows.slice(1).map((row) => {
      const values = row.split(',');
      return this.tableColumns.reduce((obj, col, index) => {
        obj[col] = values[index] || ''; // Asignar valores a cada columna
        return obj;
      }, {});
    });
    this.cdr.detectChanges();
  }

  downloadCSV(base64Data: string, fileName: string = 'reporte.csv') {
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Uint8Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const blob = new Blob([byteNumbers], { type: 'text/csv' });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  toPdf(base64: string): void {
    try {
      const byteCharacters = atob(base64);
      const byteNumbers = Array.from(byteCharacters, (char) => char.charCodeAt(0));
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(blob);
      
      // Intentar abrir automáticamente
      this.openReportInNewTab(fileURL);
      
      // Limpiar el URL después de un tiempo para liberar memoria
      setTimeout(() => {
        URL.revokeObjectURL(fileURL);
      }, 300000); // 5 minutos para dar más tiempo
      
    } catch (error) {
      console.error('Error al procesar el PDF:', error);
      this._snackBar.open('Error al procesar el archivo PDF', 'cerrar', { duration: 5000 });
    }
  }

  openReportInNewTab(url: string): void {
    // Intentar abrir la pestaña de forma robusta
    try {
      // Usar la ventana original para mantener el contexto
      const newWindow = this.originalWindow.open(url, '_blank');
      
      // Verificar si se pudo abrir la ventana
      if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        // Si no se pudo abrir, mostrar mensaje informativo
        this.showReportReadyMessage(url);
      } else {
        // Si se abrió correctamente, enfocar la nueva ventana
        newWindow.focus();
        console.log('Reporte abierto exitosamente en nueva pestaña');
      }
    } catch (error) {
      console.error('Error al abrir el reporte:', error);
      this.showReportReadyMessage(url);
    }
  }

  private showReportReadyMessage(url: string): void {
    // Mostrar mensaje permanente con opción de abrir
    this._snackBar.open(
      'El reporte es bastante extenso y puede tardar en cargar. ¿Desea abrirlo en una nueva pestaña?', 
      'Abrir Reporte', 
      {
        duration: 0 // Permanente hasta que el usuario lo cierre
      }
    ).onAction().subscribe(() => {
      // Intentar abrir la pestaña cuando el usuario hace clic
      try {
        const newWindow = this.originalWindow.open(url, '_blank');
        if (newWindow) {
          newWindow.focus();
        }
      } catch (error) {
        console.error('Error al abrir el reporte manualmente:', error);
        // Si aún falla, mostrar mensaje con URL
        this.showUrlCopyMessage(url);
      }
    });
  }

  private showUrlCopyMessage(url: string): void {
    this._snackBar.open(
      'No se pudo abrir automáticamente. Haga clic para copiar la URL y abrirla manualmente.', 
      'Copiar URL', 
      {
        duration: 0 // Permanente hasta que el usuario lo cierre
      }
    ).onAction().subscribe(() => {
      this.copyToClipboard(url);
    });
  }


  private copyToClipboard(url: string): void {
    navigator.clipboard.writeText(url).then(() => {
      this._snackBar.open('URL copiada al portapapeles', 'OK', { duration: 3000 });
    }).catch(() => {
      // Fallback para navegadores que no soportan clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      this._snackBar.open('URL copiada al portapapeles', 'OK', { duration: 3000 });
    });
  }

  exportarExcel(): void {
    if (this.tableData && this.tableData.length > 0) {
      const datosExportar = this.tableData.map((row) => {
        const mappedRow = {};
        this.tableColumns.forEach((col) => {
          const colName = col
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, (str) => str.toUpperCase())
            .trim();
          mappedRow[colName] = row[col];
        });
        return mappedRow;
      });

      this._exportService.exportToExcel(
        datosExportar,
        `${this.reportingDataFilter.reportName}_${new Date().toISOString().split('T')[0]}`
      );
    }
  }

  //#endregion

  //#region FUNCIONES

  getType(value: string) {
    switch (value) {
      case 'varchar':
        return 'text';
      case 'datetime':
        return 'date';
      case 'date':
        return 'date';
      case 'int':
        return 'number';
      case 'decimal':
        return 'number';
      default:
        return 'text';
    }
  }

  convertDateFormat(value: string, type: string): string {
    // Si es un campo de fecha y tiene el formato yyyy-mm-dd, convertir según el tipo de archivo
    if ((type === 'date' || type === 'datetime') && value && value.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = value.split('-');
      
      // Determinar formato según el tipo de archivo
      if (this.reportingDataFilter.typeFile === 'PDF') {
        // PDF: mm/dd/yyyy
        return `${month}/${day}/${year}`;
      } else {
        // CSV: dd/mm/yyyy (formato original)
        return `${day}/${month}/${year}`;
      }
    }
    return value; // Devolver el valor original si no es una fecha o no tiene el formato esperado
  }

  isDateField(label: string): boolean {
    const lowerLabel = label.toLowerCase();
    const dateKeywords = [
      'fecha', 'date', 'inicio', 'fin', 'desde', 'hasta',
      'ini', 'start', 'end', 'begin', 'final', 'inicial',
      'fechaini', 'fechafin', 'sfechaini', 'sfechafin'
    ];
    
    return dateKeywords.some(keyword => lowerLabel.includes(keyword));
  }
  goBack() {
    this.dialogRef.close();
  }

  //#endregion
}
